<template>
  <InertiaHead>
    <title>Terms of Service</title>
    <meta
      name="description"
      content="Review VibeTeams’ Terms of Service covering account use, subscriptions, payments, acceptable use, and user responsibilities on our AI collaboration platform."
    />
    <link rel="canonical" href="https://vibeteams.ai/terms-of-service" />
  </InertiaHead>

  <div class="bg-landing relative bg-cover bg-fixed bg-center bg-no-repeat">
    <!-- Navigation -->
    <nav class="fixed top-0 z-10 w-full" :class="{ 'backdrop-blur-md': isScrolled }">
      <div class="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
        <InertiaLink href="/" class="inline-block">
          <img src="@images/logo/vibeteams-logo-secondary.svg" alt="VibeTeams Logo" class="h-8" />
        </InertiaLink>
      </div>
    </nav>

    <!-- Content -->
    <div class="relative mx-auto max-w-4xl px-4 pt-[120px] pb-12 sm:px-6 lg:px-8">
      <div class="animate-fade-in space-y-8">
        <!-- Header -->
        <div class="space-y-4">
          <h1 class="font-exo-bold text-4xl text-white">Terms of Service</h1>
          <p class="text-eerie-black-200 italic">Last Updated: October 2025</p>
        </div>

        <!-- Content Sections -->
        <div class="prose max-w-none space-y-8 divide-y divide-eerie-black-400 text-gray-300 prose-invert">
          <!-- Introduction -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">1. Introduction</h2>
            <p>
              Welcome to <span class="font-segoe-bold text-white">VibeTeams.ai</span>, operated by
              <span class="font-segoe-bold text-white">CoreProc, Inc.</span> (“we,” “us,” or “our”).
            </p>
            <p>
              These Terms of Service (“Terms”) govern your access to and use of <span class="font-segoe-bold text-white">VibeTeams.ai</span>
              (the “Service”), a collaborative, no-code AI platform for building and managing intelligent agent teams.
            </p>
            <p>By accessing or using the Service, you agree to be bound by these Terms. If you do not agree, please do not use the Service.</p>
          </section>

          <!-- Account Registration -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">2. Account Registration and Responsibilities</h2>
            <p>To use the Service, you must create an account. You agree to:</p>
            <ul class="list-disc space-y-2 pl-6">
              <li>Provide accurate, current, and complete information.</li>
              <li>Keep your credentials secure and confidential.</li>
              <li>Notify us immediately of any unauthorized access or security issues.</li>
              <li>Accept full responsibility for all activities under your account.</li>
              <li>Be at least 18 years old or have parental consent to use the Service.</li>
            </ul>
            <p>You may not share, transfer, or sell your account credentials to others.</p>
          </section>

          <!-- Service Description -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">3. Service Description</h2>
            <p>VibeTeams.ai enables users to:</p>
            <ul class="list-disc space-y-2 pl-6">
              <li>Create, configure, and manage custom AI agents.</li>
              <li>Organize agents into collaborative “teams.”</li>
              <li>Conduct multi-agent conversations for insights and decision support.</li>
              <li>Access a variety of AI models provided by trusted third-party AI providers.</li>
              <li>Store chat history, agent configurations, and team data.</li>
            </ul>
            <p>We may modify, enhance, suspend, or discontinue parts of the Service at any time, with or without notice.</p>
          </section>

          <!-- Subscriptions and Payments -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">4. Subscriptions and Payments</h2>
            <h3 class="font-segoe-bold text-xl text-white">4.1 Subscription Plans</h3>
            <p>
              We offer free and paid subscription tiers. Each plan includes monthly credit allocations (“tokens”) that enable AI interactions. Premium
              plans may include:
            </p>
            <ul class="list-disc space-y-2 pl-6">
              <li>Higher token limits</li>
              <li>Increased team or agent capacity</li>
              <li>Priority support</li>
              <li>Advanced analytics and integrations</li>
              <li>API access and custom branding</li>
            </ul>

            <h3 class="font-segoe-bold text-xl text-white">4.2 Payment Processing</h3>
            <p>
              All payments are handled securely by <span class="font-segoe-bold text-white">third-party payment providers</span>. By subscribing, you
              agree to:
            </p>
            <ul class="list-disc space-y-2 pl-6">
              <li>Provide valid payment information.</li>
              <li>Authorize recurring charges per your billing cycle.</li>
              <li>Pay all applicable fees and taxes.</li>
              <li>Comply with the terms of our payment providers.</li>
            </ul>
            <p>We do not store or access full credit-card details.</p>

            <h3 class="font-segoe-bold text-xl text-white">4.3 Credit System</h3>
            <p>Your plan includes a monthly allocation of credits/tokens.</p>
            <ul class="list-disc space-y-2 pl-6">
              <li>Credits reset at the start of each billing period.</li>
              <li>
                They are <span class="font-segoe-bold text-white">non-transferable</span> and
                <span class="font-segoe-bold text-white">non-refundable</span> once used.
              </li>
              <li>Token consumption depends on AI model usage and response length.</li>
            </ul>

            <h3 class="font-segoe-bold text-xl text-white">4.4 Plan Changes and Cancellation</h3>
            <p>
              You may upgrade, downgrade, or cancel at any time through your account settings. Changes take effect at the next billing cycle unless
              otherwise noted.
            </p>
          </section>

          <!-- Acceptable Use -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">5. Acceptable Use Policy</h2>
            <p>You agree <span class="font-segoe-bold text-white">not </span>to use the Service to:</p>
            <ul class="list-disc space-y-2 pl-6">
              <li>Violate laws, regulations, or third-party rights.</li>
              <li>Generate or distribute illegal, harmful, or fraudulent content.</li>
              <li>Harass, abuse, or impersonate others.</li>
              <li>Upload malware, spam, or malicious code.</li>
              <li>Access, probe, or disrupt other accounts or systems.</li>
              <li>Reverse-engineer, resell, or create competing services.</li>
              <li>Use the Service to circumvent or violate AI provider policies.</li>
            </ul>
            <p>We reserve the right to suspend or terminate accounts that violate these terms.</p>
          </section>

          <!-- Intellectual Property -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">6. Intellectual Property Rights</h2>
            <h3 class="font-segoe-bold text-xl text-white">6.1 Our Rights</h3>
            <p>
              All Service software, design, logos, and trademarks are the property of
              <span class="font-segoe-bold text-white">CoreProc, Inc.</span> or its licensors. You may not copy, modify, or distribute them without
              written consent.
            </p>

            <h3 class="font-segoe-bold text-xl text-white">6.2 Your Content</h3>
            <p>
              You retain ownership of content you create (e.g., agent configurations, chat inputs, team data). By using the Service, you grant us a
              limited license to process, store, and display this content solely to operate the Service.
            </p>

            <h3 class="font-segoe-bold text-xl text-white">6.3 AI-Generated Content</h3>
            <p>
              AI outputs are generated by third-party AI models. Ownership and usage rights depend on those providers’ terms. We do not guarantee the
              accuracy, originality, or fitness of AI-generated content. You are responsible for verifying all outputs before use.
            </p>
          </section>

          <!-- Privacy and Data -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">7. Privacy and Data Protection</h2>
            <p>
              Your privacy is governed by our
              <InertiaLink href="/privacy-policy" class="text-navy-blue-500 hover:underline">Privacy Policy</InertiaLink>, which explains how we
              collect, use, and protect your information.
            </p>
            <p>By using the Service, you agree to those data practices.</p>
          </section>

          <!-- Third-Party Services -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">8. Third-Party Services and AI Providers</h2>
            <p>The Service integrates with trusted third-party AI providers. Use of these models is subject to:</p>
            <ul class="list-disc space-y-2 pl-6">
              <li>The provider’s own terms and policies.</li>
              <li>Applicable rate limits, content rules, and usage restrictions.</li>
            </ul>
            <p>We are not responsible for interruptions, delays, or outputs caused by these external AI systems.</p>
          </section>

          <!-- Warranties and Disclaimers -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">9. Warranties and Disclaimers</h2>
            <p>
              The Service is provided <span class="font-segoe-bold text-white">“as is”</span> and
              <span class="font-segoe-bold text-white">“as available”</span> without any warranty, express or implied. We do not guarantee that:
            </p>
            <ul class="list-disc space-y-2 pl-6">
              <li>The Service will be uninterrupted, error-free, or secure.</li>
              <li>AI-generated content will be accurate or free of bias.</li>
              <li>The Service will meet your expectations or achieve specific outcomes.</li>
            </ul>
            <p>
              You acknowledge that AI systems may produce unexpected, incomplete, or inaccurate results, and you agree to use such outputs at your own
              discretion and risk.
            </p>
          </section>

          <!-- Limitation of Liability -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">10. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, <span class="font-segoe-bold text-white">CoreProc, Inc.</span> and its affiliates shall not be
              liable for any indirect, incidental, special, consequential, or punitive damages. Our total liability for any claim arising from or
              relating to the Service shall not exceed the total amount you paid to us in the twelve (12) months preceding the claim.
            </p>
          </section>

          <!-- Indemnification -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">11. Indemnification</h2>
            <p>
              You agree to defend, indemnify, and hold harmless <span class="font-segoe-bold text-white">CoreProc, Inc.</span> and its affiliates from
              any claims, damages, or expenses (including legal fees) arising from:
            </p>
            <ul class="list-disc space-y-2 pl-6">
              <li>Your use or misuse of the Service.</li>
              <li>Your violation of these Terms.</li>
              <li>Your violation of any rights of a third party.</li>
              <li>Any content you submit or generate through the Service.</li>
            </ul>
          </section>

          <!-- Termination -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">12. Termination</h2>
            <h3 class="font-segoe-bold text-xl text-white">12.1 By You</h3>
            <p>
              You may terminate your account at any time through your account settings. Upon termination, your account and stored data may be
              permanently deleted.
            </p>

            <h3 class="font-segoe-bold text-xl text-white">12.2 By Us</h3>
            <p>We may suspend or terminate your account with or without notice if:</p>
            <ul class="list-disc space-y-2 pl-6">
              <li>You violate these Terms.</li>
              <li>Your account is inactive for an extended period.</li>
              <li>We are required by law to do so.</li>
              <li>Continued use creates legal or security risk.</li>
            </ul>

            <h3 class="font-segoe-bold text-xl text-white">12.3 Effects of Termination</h3>
            <p>
              Upon termination, all licenses and access rights end immediately. Certain sections (e.g., Disclaimers, Limitation of Liability,
              Indemnification) will continue to apply.
            </p>
          </section>

          <!-- Changes to Terms -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">13. Changes to These Terms</h2>
            <p>We may update these Terms from time to time. When we do:</p>
            <ul class="list-disc space-y-2 pl-6">
              <li>We will update the “Last Updated” date.</li>
              <li>Notify you via email or in-app alert for material changes.</li>
              <li>Continued use after updates means you accept the revised Terms.</li>
            </ul>
          </section>

          <!-- Governing Law -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">14. Governing Law and Dispute Resolution</h2>
            <p>
              These Terms are governed by the laws of the jurisdiction where <span class="font-segoe-bold text-white">CoreProc, Inc.</span> is
              registered (Philippines), without regard to conflict-of-law principles. Disputes will be resolved through:
            </p>
            <ol class="list-decimal space-y-2 pl-6">
              <li>Good-faith negotiation;</li>
              <li>Mediation, if needed;</li>
              <li>Binding arbitration or small-claims court, as appropriate.</li>
            </ol>
            <p>You waive any right to participate in class-action lawsuits or collective claims.</p>
          </section>

          <!-- Miscellaneous -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">15. Miscellaneous</h2>
            <ul class="list-disc space-y-2 pl-6">
              <li>
                <strong>Entire Agreement:</strong> These Terms, along with the Privacy Policy and Refund Policy, form the full agreement between you
                and us.
              </li>
              <li><strong>Severability:</strong> If a provision is found unenforceable, the rest remain in effect.</li>
              <li><strong>Waiver:</strong> Our failure to enforce a right does not waive it.</li>
              <li>
                <strong>Assignment:</strong> You may not transfer your rights without written consent. We may assign these Terms in the event of
                reorganization or acquisition.
              </li>
            </ul>
          </section>

          <!-- Contact -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">16. Contact Information</h2>
            <p>For questions about these Terms, contact:</p>
            <p class="font-segoe-bold text-white">CoreProc, Inc.</p>
            <div class="grid">
              <a href="mailto:hello@vibeteams.ai" class="flex w-fit items-center gap-1 no-underline"><Mail />hello@vibeteams.ai</a>
              <a href="https://vibeteams.ai" class="flex w-fit items-center gap-1 text-navy-blue-500 hover:underline"
                ><Globe /> https://vibeteams.ai</a
              >
            </div>
          </section>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <NavFooter />
  </div>
</template>

<script lang="ts">
import NavFooter from '@/components/landing/Footer.vue';
import { Head as InertiaHead } from '@inertiajs/vue3';
import { Globe, Mail } from 'lucide-vue-next';

export default {
  components: {
    NavFooter,
    InertiaHead,
    Mail,
    Globe,
  },
  data() {
    return {
      isScrolled: false,
    };
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
  },
  unmounted() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    handleScroll() {
      const scrolled = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollPercent = (scrolled / (documentHeight - windowHeight)) * 100;

      this.isScrolled = scrollPercent > 1;
    },
  },
};
</script>

<style>
.bg-landing {
  background-image: url('@images/landing-page/landing-bg-asset.webp');
}
</style>
